/**
 * MenuPreviewDrawer — 역할 선택 → 해당 역할에게 보이는 메뉴 트리 시뮬레이션.
 *
 * #472: flat RBAC — ROLE_RANK 서열 비교 제거. 백엔드 assembleTree 와 동일한
 * flat predicate 적용:
 *   1. menu.visible === true (조상도 visible 이어야 함 — 트리 게이팅)
 *   2. menu.requiredPermission 이 있으면 → 역할의 permission set 에 포함 여부
 *      없으면 → requiredRole 이 null/blank/'ROLE_GUEST' 면 공개
 *              아니면 → 역할 코드가 requiredRole 과 정확 일치
 *
 * 역할 선택자는 GET /api/eraf/roles 를 통해 동적으로 불러온 목록을 사용한다
 * (ROLE_EXEC 등 커스텀 역할 포함).
 */
import { useState } from 'react';
import type { MenuTreeNode, MyMenuItem } from '../types/menu';
import { useRoleListForPreview } from '../hooks/useRoleListForPreview';
import { useRoleDetailForPreview } from '../hooks/useRoleDetailForPreview';

interface MenuPreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  treeNodes: MenuTreeNode[];
}

/**
 * flat predicate — 백엔드 MenuVisibilityResolver (flat RBAC, #472) 와 동일한 로직.
 * @param node 검사 대상 메뉴 노드
 * @param roleCode 선택된 역할 코드 (예: 'ROLE_ADMIN')
 * @param permSet 해당 역할이 보유한 permission code Set
 */
function isVisibleFlat(
  node: MenuTreeNode,
  roleCode: string,
  permSet: Set<string>
): boolean {
  // 1. visible 게이팅
  if (!node.visible) return false;

  // 2. requiredPermission 분기
  if (node.requiredPermission) {
    return permSet.has(node.requiredPermission);
  }

  // 3. requiredRole 분기 (flat — 정확 일치만, 서열 상속 없음)
  const required = node.requiredRole;
  if (!required || required === '' || required === 'ROLE_GUEST') {
    // 공개 메뉴
    return true;
  }
  return roleCode === required;
}

/**
 * filterByRoleFlat — 트리를 재귀적으로 순회하며 flat predicate 를 적용.
 * 조상이 보여야 자식도 평가한다 (ancestor gating).
 */
function filterByRoleFlat(
  nodes: MenuTreeNode[],
  roleCode: string,
  permSet: Set<string>
): MyMenuItem[] {
  const result: MyMenuItem[] = [];
  for (const node of nodes) {
    if (!isVisibleFlat(node, roleCode, permSet)) continue;

    const filteredChildren = filterByRoleFlat(node.children ?? [], roleCode, permSet);
    result.push({
      menuKey: node.menuKey,
      nameKo: node.nameKo,
      nameEn: node.nameEn,
      labelKey: node.labelKey,
      url: node.url,
      icon: node.icon,
      menuType: node.menuType,
      depth: node.depth,
      sortOrder: node.sortOrder,
      children: filteredChildren,
    });
  }
  return result;
}

/** 트리를 flat 순회하여 총 노드 수 계산 */
function countNodes(nodes: MyMenuItem[]): number {
  return nodes.reduce((acc, n) => acc + 1 + countNodes(n.children ?? []), 0);
}

/* ──────────────────── PreviewNode ──────────────────── */

interface PreviewNodeProps {
  node: MyMenuItem;
  depth: number;
}

function PreviewNode({ node, depth }: PreviewNodeProps) {
  const indentPx = depth * 16;
  return (
    <>
      <li
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-1) var(--spacing-3)',
          paddingLeft: `calc(var(--spacing-3) + ${indentPx}px)`,
          fontSize: 'var(--font-size-sm)',
          color:
            node.menuType === 'DIVIDER'
              ? 'var(--color-text-muted)'
              : 'var(--color-text-primary)',
          fontWeight: node.menuType === 'DIVIDER' ? 600 : 400,
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 'var(--font-size-xs)' }}>
          {node.menuType === 'DIVIDER'
            ? '📁'
            : node.menuType === 'EXTERNAL'
            ? '🔗'
            : '📄'}
        </span>
        <span style={{ flex: 1 }}>{node.nameKo}</span>
        {node.url && (
          <span
            style={{
              fontSize: '10px',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 120,
              whiteSpace: 'nowrap',
            }}
          >
            {node.url}
          </span>
        )}
      </li>
      {node.children.map((child) => (
        <PreviewNode key={child.menuKey} node={child} depth={depth + 1} />
      ))}
    </>
  );
}

/* ──────────────────── MenuPreviewDrawer ──────────────────── */

export function MenuPreviewDrawer({ open, onClose, treeNodes }: MenuPreviewDrawerProps) {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>('ROLE_ADMIN');

  // 동적 역할 목록 (GET /api/eraf/roles)
  const rolesQuery = useRoleListForPreview();
  const roles = rolesQuery.data ?? [];

  // 첫 번째 로드 시 roles 가 오면 기본 선택을 ROLE_ADMIN 으로 맞춤
  // (id 가 null 인 상태에서 detail fetch 방지)
  const selectedRole = roles.find((r) => r.code === selectedRoleCode) ?? roles[0] ?? null;
  const effectiveRoleId = selectedRole?.id ?? selectedRoleId;

  // 선택된 역할의 permission 목록 (GET /api/eraf/roles/{id})
  const roleDetailQuery = useRoleDetailForPreview(effectiveRoleId ?? null);
  const permSet = new Set<string>(
    roleDetailQuery.data?.permissions?.map((p) => p.code) ?? []
  );

  const effectiveRoleCode = selectedRole?.code ?? selectedRoleCode;
  const previewMenus = filterByRoleFlat(treeNodes, effectiveRoleCode, permSet);

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setSelectedRoleCode(code);
    const found = roles.find((r) => r.code === code);
    setSelectedRoleId(found?.id ?? null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  if (!open) return null;

  const isLoadingRoles = rolesQuery.isLoading;
  const isLoadingPerms = roleDetailQuery.isFetching;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 200,
        }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="역할별 메뉴 미리보기"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 360,
          maxWidth: '100vw',
          background: 'var(--color-surface-background)',
          borderLeft: '1px solid var(--color-surface-border)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              flex: 1,
            }}
          >
            역할별 메뉴 미리보기
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="미리보기 닫기"
            style={{
              background: 'none',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-1) var(--spacing-2)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            닫기
          </button>
        </div>

        {/* Role selector */}
        <div style={{ padding: 'var(--spacing-3) var(--spacing-4)', flexShrink: 0 }}>
          <label
            htmlFor="preview-role-select"
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              display: 'block',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            역할 선택
          </label>
          <select
            id="preview-role-select"
            value={effectiveRoleCode}
            onChange={handleRoleChange}
            disabled={isLoadingRoles}
            aria-busy={isLoadingRoles}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              opacity: isLoadingRoles ? 0.6 : 1,
            }}
          >
            {isLoadingRoles ? (
              <option value="">역할 목록 로딩 중...</option>
            ) : (
              roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.nameKo} ({r.code})
                </option>
              ))
            )}
          </select>
          {/* permission 로딩 인디케이터 */}
          {isLoadingPerms && (
            <p
              role="status"
              aria-live="polite"
              style={{
                margin: 'var(--spacing-1) 0 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              권한 정보 로딩 중...
            </p>
          )}
        </div>

        {/* Preview result */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 var(--spacing-4)' }}>
          {previewMenus.length === 0 ? (
            <div
              style={{
                padding: 'var(--spacing-8)',
                textAlign: 'center',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              이 역할에 노출될 메뉴가 없습니다
            </div>
          ) : (
            <ul
              role="list"
              aria-label={`${effectiveRoleCode} 기준 메뉴 트리`}
              style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
              {previewMenus.map((node) => (
                <PreviewNode key={node.menuKey} node={node} depth={0} />
              ))}
            </ul>
          )}
        </div>

        {/* Footer count */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderTop: '1px solid var(--color-surface-border)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            flexShrink: 0,
          }}
        >
          {effectiveRoleCode}에 보이는 메뉴: {countNodes(previewMenus)}개
        </div>
      </aside>
    </>
  );
}
